/**
 * Policy Recommendations Page (F051 T4.1)
 *
 * Route: /policy-intelligence/recommendations
 * Nav: Enforcement > Policy Intelligence > Recommendations
 *
 * Card-based layout with filter toolbar for browsing, accepting, dismissing,
 * and deferring policy recommendations. Wired entirely to live API data — an
 * empty response renders the explicit empty state, never a fabricated
 * fallback value.
 */
import React, { useState, useMemo } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  Dropdown,
  Option,
  Spinner,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
  ClockRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import {
  usePolicyRecommendations,
  useAcceptRecommendation,
  useDismissRecommendation,
  useDeferRecommendation,
} from '../hooks/usePolicyIntelligence';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  filterField: {
    flex: '1 1 auto',
    minWidth: '11.25rem',
  },
  recCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
  },
  recHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  recBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  recSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  recFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  recActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  recMeta: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getImpactColor = (impact: string): 'danger' | 'warning' | 'success' => {
  switch (impact) {
    case 'High': return 'danger';
    case 'Medium': return 'warning';
    case 'Low': return 'success';
    default: return 'warning';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Accepted': return <CheckmarkRegular />;
    case 'Dismissed': return <DismissRegular />;
    default: return <ClockRegular />;
  }
};

const getStatusColor = (status: string): 'success' | 'danger' | 'informative' => {
  switch (status) {
    case 'Accepted': return 'success';
    case 'Dismissed': return 'danger';
    default: return 'informative';
  }
};

const formatDate = (iso: string): string => {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Default defer window — mirrors the 30-day resurface window already used for dismissed recommendations. */
const DEFAULT_DEFER_DAYS = 30;

const defaultDeferUntil = (): string =>
  new Date(Date.now() + DEFAULT_DEFER_DAYS * 24 * 60 * 60 * 1000).toISOString();

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

const typeOptions = ['All', 'Tuning', 'Promotion', 'New Policy', 'Consolidation', 'Retirement', 'Automation'];
const statusOptions = ['All', 'Pending', 'Accepted', 'Dismissed'];
const familyOptions = ['All', 'IAM', 'Secrets', 'Supply Chain', 'Network', 'Observability', 'Configuration', 'Data Protection', 'Legal'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PolicyRecommendationsPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();

  const { data: recsData, isLoading, error } = usePolicyRecommendations();
  const acceptMutation = useAcceptRecommendation();
  const dismissMutation = useDismissRecommendation();
  const deferMutation = useDeferRecommendation();

  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [familyFilter, setFamilyFilter] = useState('All');

  // An empty/unavailable API response renders the explicit empty state below — never a
  // fabricated fallback value.
  const allRecs = recsData?.recommendations ?? [];

  const filteredRecs = useMemo(() => {
    return allRecs.filter((r) => {
      if (typeFilter !== 'All' && r.type !== typeFilter) return false;
      if (statusFilter !== 'All' && r.status !== statusFilter) return false;
      if (familyFilter !== 'All' && r.policyFamily !== familyFilter) return false;
      return true;
    });
  }, [allRecs, typeFilter, statusFilter, familyFilter]);

  // Loading state
  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Policy Recommendations"
          subtitle="AI-driven recommendations to improve your governance posture"
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner size="large" label="Loading recommendations..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !recsData) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Policy Recommendations"
          subtitle="AI-driven recommendations to improve your governance posture"
        />
        <Body1 style={{ color: tokens.colorPaletteRedForeground1, textAlign: 'center', padding: tokens.spacingVerticalXXL }}>
          Failed to load recommendations. Please try again later.
        </Body1>
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Policy Recommendations"
        subtitle="AI-driven recommendations to improve your governance posture"
      />

      {/* ── Filter Toolbar ──────────────────────────────────────────── */}
      <Card>
        <div className={ps.cardBody}>
          <div className={styles.filterBar}>
            <div className={styles.filterField}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: tokens.spacingVerticalXXS }}>Type</Caption1>
              <Dropdown
                value={typeFilter}
                onOptionSelect={(_e, data) => setTypeFilter(data.optionValue ?? 'All')}
              >
                {typeOptions.map((opt) => (
                  <Option key={opt} value={opt}>{opt}</Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.filterField}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: tokens.spacingVerticalXXS }}>Status</Caption1>
              <Dropdown
                value={statusFilter}
                onOptionSelect={(_e, data) => setStatusFilter(data.optionValue ?? 'All')}
              >
                {statusOptions.map((opt) => (
                  <Option key={opt} value={opt}>{opt}</Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.filterField}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: tokens.spacingVerticalXXS }}>Policy Family</Caption1>
              <Dropdown
                value={familyFilter}
                onOptionSelect={(_e, data) => setFamilyFilter(data.optionValue ?? 'All')}
              >
                {familyOptions.map((opt) => (
                  <Option key={opt} value={opt}>{opt}</Option>
                ))}
              </Dropdown>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Recommendation Cards ────────────────────────────────────── */}
      {filteredRecs.length === 0 ? (
        <div className={ps.emptyState}>
          <Title2>No Recommendations</Title2>
          <Body1 style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS }}>
            Your governance posture is strong. All policies are performing within acceptable thresholds.
          </Body1>
        </div>
      ) : (
        <div className={styles.cardList}>
          {filteredRecs.map((rec) => (
            <Card key={rec.id}>
              <div className={styles.recCard}>
                {/* Header: title + badges */}
                <div className={styles.recHeader}>
                  <Body1 style={{ fontWeight: 600 }}>{rec.title}</Body1>
                  <div className={styles.recBadges}>
                    <Badge appearance="filled" color={getImpactColor(rec.estimatedImpact)} size="small">
                      {rec.estimatedImpact} Impact
                    </Badge>
                    <Badge appearance="outline" color={getStatusColor(rec.status)} size="small" icon={getStatusIcon(rec.status)}>
                      {rec.status}
                    </Badge>
                  </div>
                </div>

                {/* Why this matters */}
                <div className={styles.recSection}>
                  <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Why this matters</Caption1>
                  <Body2 style={{ color: tokens.colorNeutralForeground2 }}>{rec.whyItMatters}</Body2>
                </div>

                {/* Suggested action */}
                <div className={styles.recSection}>
                  <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Suggested action</Caption1>
                  <Body2>{rec.suggestedAction}</Body2>
                </div>

                {/* Footer: meta + actions */}
                <div className={styles.recFooter}>
                  <div className={styles.recMeta}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Confidence: {rec.confidence}%
                    </Caption1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Family: {rec.policyFamily}
                    </Caption1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Type: {rec.type}
                    </Caption1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      {formatDate(rec.createdAt)}
                    </Caption1>
                  </div>
                  {rec.status === 'Pending' && (
                    <div className={styles.recActions}>
                      <Button
                        appearance="primary"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={acceptMutation.isPending}
                        onClick={() => acceptMutation.mutate(rec.id)}
                      >
                        Accept
                      </Button>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        disabled={dismissMutation.isPending}
                        onClick={() => dismissMutation.mutate(rec.id)}
                      >
                        Dismiss
                      </Button>
                      <Button
                        appearance="outline"
                        size="small"
                        icon={<ClockRegular />}
                        disabled={deferMutation.isPending}
                        onClick={() => deferMutation.mutate({ id: rec.id, deferUntil: defaultDeferUntil() })}
                      >
                        Defer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
